// Demo data for the interactive demo page
// Contains fake artist profile and 150 classic rock songs

export interface DemoSong {
  id: string
  title: string
  artist: string
  artworkUrl: string | null
  durationMs: number
}

export interface DemoArtist {
  id: string
  handle: string
  name: string
  bio: string
  profileImageUrl: string | null
  coverImageUrl: string | null
  socialLinks: Array<{
    id: string
    platform: string
    url: string
    displayName: string | null
  }>
}

export interface DemoEvent {
  id: string
  title: string
  venue: string | null
  startTime: Date
  endTime: Date | null
  status: 'upcoming' | 'live'
}

// Generate a placeholder artwork as an inline SVG data URI (no external dependency)
function getArtworkUrl(title: string, artist: string): string {
  const initials = `${title[0]}${artist[0]}`.toUpperCase()
  const hash = (title + artist).split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0)
    return a & a
  }, 0)
  const hue = Math.abs(hash) % 360
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <rect width="300" height="300" fill="hsl(${hue},50%,25%)"/>
    <text x="150" y="160" text-anchor="middle" font-family="system-ui,sans-serif" font-size="96" font-weight="bold" fill="hsl(${hue},60%,70%)">${initials}</text>
  </svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// Generate random duration between 3-6 minutes
function randomDuration(): number {
  return (180 + Math.floor(Math.random() * 180)) * 1000 // 3-6 minutes in ms
}

// The demo artist (use createDemoArtist() for translated version)
export const DEMO_ARTIST: DemoArtist = {
  id: 'demo-artist-001',
  handle: 'demo-artist',
  name: 'The Classic Rockers',
  bio: 'Your favorite classic rock covers band! From Led Zeppelin to Queen, we play all the hits that made rock legendary. Join us every weekend for an unforgettable live experience.',
  profileImageUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=80&fit=crop',
  coverImageUrl: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=1200&q=80&fit=crop',
  socialLinks: [
    { id: 'sl-1', platform: 'instagram', url: 'https://instagram.com/theclassicrockers', displayName: '@theclassicrockers' },
    { id: 'sl-2', platform: 'spotify', url: 'https://spotify.com', displayName: 'The Classic Rockers' },
    { id: 'sl-3', platform: 'youtube', url: 'https://youtube.com', displayName: 'The Classic Rockers' },
  ],
}

/** Create a translated demo artist */
export function createDemoArtist(bio: string): DemoArtist {
  return { ...DEMO_ARTIST, bio }
}

// Generate a contextual event title based on current day/time
function generateEventTitle(): string {
  const { dayKey, slotKey } = getEventTitleKeys()
  // Fallback English values for non-translated contexts
  const days: Record<string, string> = {
    sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday',
  }
  const slots: Record<string, string> = {
    brunchJams: 'Brunch Jams', morningMelodies: 'Morning Melodies', riseAndRock: 'Rise & Rock',
    afternoonAcoustics: 'Afternoon Acoustics', middayClassics: 'Midday Classics', afternoonTunes: 'Afternoon Tunes',
    happyHourHits: 'Happy Hour Hits', sunsetSessions: 'Sunset Sessions', eveningGrooves: 'Evening Grooves',
    nightRocks: 'Night Rocks', lateNightClassics: 'Late Night Classics', afterDarkJams: 'After Dark Jams',
  }
  return `${days[dayKey]} ${slots[slotKey]}`
}

/** Returns the translation keys for the current event title (day + time slot) */
export function getEventTitleKeys(): { dayKey: string; slotKey: string } {
  const now = new Date()
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const dayKey = dayKeys[now.getDay()]

  const hour = now.getHours()
  const timeSlotKeys: Record<string, string[]> = {
    morning: ['brunchJams', 'morningMelodies', 'riseAndRock'],
    afternoon: ['afternoonAcoustics', 'middayClassics', 'afternoonTunes'],
    evening: ['happyHourHits', 'sunsetSessions', 'eveningGrooves'],
    night: ['nightRocks', 'lateNightClassics', 'afterDarkJams'],
  }

  const slot = hour < 11 ? 'morning' : hour < 16 ? 'afternoon' : hour < 20 ? 'evening' : 'night'
  const options = timeSlotKeys[slot]
  const slotKey = options[now.getDay() % options.length]

  return { dayKey, slotKey }
}

/** Generate a translated event title */
export function generateTranslatedEventTitle(dayLabel: string, slotLabel: string): string {
  return `${dayLabel} ${slotLabel}`
}

// The demo event (starts as upcoming, transitions to live)
export const DEMO_EVENT: DemoEvent = {
  id: 'demo-event-001',
  title: generateEventTitle(),
  venue: 'The Blue Note Lounge',
  startTime: new Date(Date.now() + 3 * 60 * 1000), // Starts in 3 minutes
  endTime: new Date(Date.now() + 3 * 60 * 60 * 1000), // Ends in 3 hours
  status: 'upcoming',
}

/** Create a translated demo event */
export function createDemoEvent(title: string, venue: string): DemoEvent {
  return { ...DEMO_EVENT, title, venue }
}

// Curated featured songs for pre-event artist profile
// Will be populated after DEMO_SONGS initialization
export let DEMO_FEATURED_SONGS: DemoSong[] = []

// 150 Classic Rock Songs
const CLASSIC_ROCK_CATALOG: Array<{ title: string; artist: string }> = [
  // Led Zeppelin
  { title: 'Stairway to Heaven', artist: 'Led Zeppelin' },
  { title: 'Whole Lotta Love', artist: 'Led Zeppelin' },
  { title: 'Kashmir', artist: 'Led Zeppelin' },
  { title: 'Black Dog', artist: 'Led Zeppelin' },
  { title: 'Rock and Roll', artist: 'Led Zeppelin' },
  { title: 'Immigrant Song', artist: 'Led Zeppelin' },
  
  // Queen
  { title: 'Bohemian Rhapsody', artist: 'Queen' },
  { title: 'We Will Rock You', artist: 'Queen' },
  { title: 'We Are the Champions', artist: 'Queen' },
  { title: 'Somebody to Love', artist: 'Queen' },
  { title: 'Don\'t Stop Me Now', artist: 'Queen' },
  { title: 'Another One Bites the Dust', artist: 'Queen' },
  { title: 'Under Pressure', artist: 'Queen' },
  { title: 'Radio Ga Ga', artist: 'Queen' },
  
  // The Eagles
  { title: 'Hotel California', artist: 'Eagles' },
  { title: 'Take It Easy', artist: 'Eagles' },
  { title: 'Desperado', artist: 'Eagles' },
  { title: 'Life in the Fast Lane', artist: 'Eagles' },
  { title: 'One of These Nights', artist: 'Eagles' },
  
  // Pink Floyd
  { title: 'Comfortably Numb', artist: 'Pink Floyd' },
  { title: 'Wish You Were Here', artist: 'Pink Floyd' },
  { title: 'Another Brick in the Wall', artist: 'Pink Floyd' },
  { title: 'Money', artist: 'Pink Floyd' },
  { title: 'Time', artist: 'Pink Floyd' },
  
  // AC/DC
  { title: 'Back in Black', artist: 'AC/DC' },
  { title: 'Highway to Hell', artist: 'AC/DC' },
  { title: 'Thunderstruck', artist: 'AC/DC' },
  { title: 'You Shook Me All Night Long', artist: 'AC/DC' },
  { title: 'T.N.T.', artist: 'AC/DC' },
  { title: 'For Those About to Rock', artist: 'AC/DC' },
  
  // Guns N' Roses
  { title: 'Sweet Child O\' Mine', artist: 'Guns N\' Roses' },
  { title: 'Welcome to the Jungle', artist: 'Guns N\' Roses' },
  { title: 'Paradise City', artist: 'Guns N\' Roses' },
  { title: 'November Rain', artist: 'Guns N\' Roses' },
  { title: 'Patience', artist: 'Guns N\' Roses' },
  
  // The Rolling Stones
  { title: 'Paint It Black', artist: 'The Rolling Stones' },
  { title: 'Satisfaction', artist: 'The Rolling Stones' },
  { title: 'Sympathy for the Devil', artist: 'The Rolling Stones' },
  { title: 'Gimme Shelter', artist: 'The Rolling Stones' },
  { title: 'Start Me Up', artist: 'The Rolling Stones' },
  { title: 'Brown Sugar', artist: 'The Rolling Stones' },
  
  // The Beatles
  { title: 'Come Together', artist: 'The Beatles' },
  { title: 'Let It Be', artist: 'The Beatles' },
  { title: 'Hey Jude', artist: 'The Beatles' },
  { title: 'Yesterday', artist: 'The Beatles' },
  { title: 'Here Comes the Sun', artist: 'The Beatles' },
  { title: 'A Hard Day\'s Night', artist: 'The Beatles' },
  { title: 'Twist and Shout', artist: 'The Beatles' },
  
  // Aerosmith
  { title: 'Dream On', artist: 'Aerosmith' },
  { title: 'Walk This Way', artist: 'Aerosmith' },
  { title: 'I Don\'t Want to Miss a Thing', artist: 'Aerosmith' },
  { title: 'Sweet Emotion', artist: 'Aerosmith' },
  { title: 'Crazy', artist: 'Aerosmith' },
  
  // Bon Jovi
  { title: 'Livin\' on a Prayer', artist: 'Bon Jovi' },
  { title: 'You Give Love a Bad Name', artist: 'Bon Jovi' },
  { title: 'Wanted Dead or Alive', artist: 'Bon Jovi' },
  { title: 'It\'s My Life', artist: 'Bon Jovi' },
  { title: 'Bad Medicine', artist: 'Bon Jovi' },
  
  // Journey
  { title: 'Don\'t Stop Believin\'', artist: 'Journey' },
  { title: 'Separate Ways', artist: 'Journey' },
  { title: 'Open Arms', artist: 'Journey' },
  { title: 'Any Way You Want It', artist: 'Journey' },
  { title: 'Wheel in the Sky', artist: 'Journey' },
  
  // Def Leppard
  { title: 'Pour Some Sugar on Me', artist: 'Def Leppard' },
  { title: 'Photograph', artist: 'Def Leppard' },
  { title: 'Love Bites', artist: 'Def Leppard' },
  { title: 'Rock of Ages', artist: 'Def Leppard' },
  { title: 'Hysteria', artist: 'Def Leppard' },
  
  // Van Halen
  { title: 'Jump', artist: 'Van Halen' },
  { title: 'Panama', artist: 'Van Halen' },
  { title: 'Hot for Teacher', artist: 'Van Halen' },
  { title: 'Runnin\' with the Devil', artist: 'Van Halen' },
  { title: 'Eruption', artist: 'Van Halen' },
  
  // The Who
  { title: 'Baba O\'Riley', artist: 'The Who' },
  { title: 'Won\'t Get Fooled Again', artist: 'The Who' },
  { title: 'My Generation', artist: 'The Who' },
  { title: 'Behind Blue Eyes', artist: 'The Who' },
  { title: 'Pinball Wizard', artist: 'The Who' },
  
  // Fleetwood Mac
  { title: 'The Chain', artist: 'Fleetwood Mac' },
  { title: 'Go Your Own Way', artist: 'Fleetwood Mac' },
  { title: 'Dreams', artist: 'Fleetwood Mac' },
  { title: 'Landslide', artist: 'Fleetwood Mac' },
  { title: 'Rhiannon', artist: 'Fleetwood Mac' },
  
  // Lynyrd Skynyrd
  { title: 'Sweet Home Alabama', artist: 'Lynyrd Skynyrd' },
  { title: 'Free Bird', artist: 'Lynyrd Skynyrd' },
  { title: 'Simple Man', artist: 'Lynyrd Skynyrd' },
  { title: 'Tuesday\'s Gone', artist: 'Lynyrd Skynyrd' },
  
  // Deep Purple
  { title: 'Smoke on the Water', artist: 'Deep Purple' },
  { title: 'Highway Star', artist: 'Deep Purple' },
  { title: 'Child in Time', artist: 'Deep Purple' },
  { title: 'Black Night', artist: 'Deep Purple' },
  
  // Black Sabbath
  { title: 'Iron Man', artist: 'Black Sabbath' },
  { title: 'Paranoid', artist: 'Black Sabbath' },
  { title: 'War Pigs', artist: 'Black Sabbath' },
  { title: 'N.I.B.', artist: 'Black Sabbath' },
  
  // Jimi Hendrix
  { title: 'Purple Haze', artist: 'Jimi Hendrix' },
  { title: 'All Along the Watchtower', artist: 'Jimi Hendrix' },
  { title: 'Voodoo Child', artist: 'Jimi Hendrix' },
  { title: 'Hey Joe', artist: 'Jimi Hendrix' },
  { title: 'Little Wing', artist: 'Jimi Hendrix' },
  
  // The Doors
  { title: 'Light My Fire', artist: 'The Doors' },
  { title: 'Riders on the Storm', artist: 'The Doors' },
  { title: 'Break On Through', artist: 'The Doors' },
  { title: 'People Are Strange', artist: 'The Doors' },
  { title: 'L.A. Woman', artist: 'The Doors' },
  
  // Creedence Clearwater Revival
  { title: 'Fortunate Son', artist: 'CCR' },
  { title: 'Bad Moon Rising', artist: 'CCR' },
  { title: 'Have You Ever Seen the Rain', artist: 'CCR' },
  { title: 'Proud Mary', artist: 'CCR' },
  { title: 'Down on the Corner', artist: 'CCR' },
  
  // ZZ Top
  { title: 'La Grange', artist: 'ZZ Top' },
  { title: 'Sharp Dressed Man', artist: 'ZZ Top' },
  { title: 'Legs', artist: 'ZZ Top' },
  { title: 'Tush', artist: 'ZZ Top' },
  
  // Boston
  { title: 'More Than a Feeling', artist: 'Boston' },
  { title: 'Peace of Mind', artist: 'Boston' },
  { title: 'Foreplay/Long Time', artist: 'Boston' },
  { title: 'Amanda', artist: 'Boston' },
  
  // Foreigner
  { title: 'I Want to Know What Love Is', artist: 'Foreigner' },
  { title: 'Hot Blooded', artist: 'Foreigner' },
  { title: 'Cold as Ice', artist: 'Foreigner' },
  { title: 'Juke Box Hero', artist: 'Foreigner' },
  { title: 'Waiting for a Girl Like You', artist: 'Foreigner' },
  
  // REO Speedwagon
  { title: 'Keep On Loving You', artist: 'REO Speedwagon' },
  { title: 'Can\'t Fight This Feeling', artist: 'REO Speedwagon' },
  { title: 'Take It on the Run', artist: 'REO Speedwagon' },
  
  // Kansas
  { title: 'Carry On Wayward Son', artist: 'Kansas' },
  { title: 'Dust in the Wind', artist: 'Kansas' },
  { title: 'Point of Know Return', artist: 'Kansas' },
  
  // Styx
  { title: 'Come Sail Away', artist: 'Styx' },
  { title: 'Mr. Roboto', artist: 'Styx' },
  { title: 'Renegade', artist: 'Styx' },
  { title: 'Too Much Time on My Hands', artist: 'Styx' },
  
  // Heart
  { title: 'Barracuda', artist: 'Heart' },
  { title: 'Alone', artist: 'Heart' },
  { title: 'Crazy on You', artist: 'Heart' },
  { title: 'What About Love', artist: 'Heart' },
  
  // Tom Petty
  { title: 'Free Fallin\'', artist: 'Tom Petty' },
  { title: 'American Girl', artist: 'Tom Petty' },
  { title: 'I Won\'t Back Down', artist: 'Tom Petty' },
  { title: 'Runnin\' Down a Dream', artist: 'Tom Petty' },
  { title: 'Mary Jane\'s Last Dance', artist: 'Tom Petty' },
  
  // Bruce Springsteen
  { title: 'Born to Run', artist: 'Bruce Springsteen' },
  { title: 'Born in the U.S.A.', artist: 'Bruce Springsteen' },
  { title: 'Dancing in the Dark', artist: 'Bruce Springsteen' },
  { title: 'Thunder Road', artist: 'Bruce Springsteen' },
  { title: 'Glory Days', artist: 'Bruce Springsteen' },
  
  // Eric Clapton
  { title: 'Layla', artist: 'Eric Clapton' },
  { title: 'Wonderful Tonight', artist: 'Eric Clapton' },
  { title: 'Cocaine', artist: 'Eric Clapton' },
  { title: 'Tears in Heaven', artist: 'Eric Clapton' },
  
  // Santana
  { title: 'Black Magic Woman', artist: 'Santana' },
  { title: 'Smooth', artist: 'Santana' },
  { title: 'Oye Como Va', artist: 'Santana' },
  
  // Ozzy Osbourne
  { title: 'Crazy Train', artist: 'Ozzy Osbourne' },
  { title: 'Mr. Crowley', artist: 'Ozzy Osbourne' },
  { title: 'Bark at the Moon', artist: 'Ozzy Osbourne' },
  
  // Whitesnake
  { title: 'Here I Go Again', artist: 'Whitesnake' },
  { title: 'Is This Love', artist: 'Whitesnake' },
  { title: 'Still of the Night', artist: 'Whitesnake' },
]

// Generate the full song list with IDs, artwork URLs, and durations
export const DEMO_SONGS: DemoSong[] = CLASSIC_ROCK_CATALOG.map((song, index) => ({
  id: `demo-song-${String(index + 1).padStart(3, '0')}`,
  title: song.title,
  artist: song.artist,
  artworkUrl: getArtworkUrl(song.title, song.artist),
  durationMs: randomDuration(),
}))

// Initial queue with a few requests already in place
export const INITIAL_QUEUE = [
  { songId: 'demo-song-001', upvotes: 3, totalTipsCents: 500 }, // Stairway to Heaven
  { songId: 'demo-song-007', upvotes: 5, totalTipsCents: 0 },   // Bohemian Rhapsody
  { songId: 'demo-song-026', upvotes: 2, totalTipsCents: 200 }, // Back in Black
]

// Get a random song that's not already in queue
export function getRandomSongNotInQueue(queueSongIds: string[]): DemoSong | null {
  const available = DEMO_SONGS.filter(s => !queueSongIds.includes(s.id))
  if (available.length === 0) return null
  return available[Math.floor(Math.random() * available.length)]
}

// Get a random song from the catalog
export function getRandomSong(): DemoSong {
  return DEMO_SONGS[Math.floor(Math.random() * DEMO_SONGS.length)]
}

// Setlist interfaces
export interface DemoSetlist {
  id: string
  name: string
  songs: Array<{
    position: number
    songId: string
    song: DemoSong
  }>
  totalDurationMs: number
}

// Event setlist (active setlist for event with progress tracking)
export interface DemoEventSetlist {
  setlistId: string
  setlist: DemoSetlist
  currentPosition: number
  allSongs: Array<{
    position: number
    songId: string
    song: DemoSong
    isPlayed: boolean
    isCurrentlyPlaying: boolean
    isNext: boolean
  }>
  totalSongs: number
  songsPlayed: number
  remainingDurationMs: number
  isComplete: boolean
}

// Populate featured songs for pre-event profile (6 curated crowd favorites)
DEMO_FEATURED_SONGS = [
  DEMO_SONGS[0],   // Stairway to Heaven
  DEMO_SONGS[6],   // Bohemian Rhapsody
  DEMO_SONGS[25],  // Back in Black
  DEMO_SONGS[12],  // Hotel California
  DEMO_SONGS[30],  // Sweet Child O' Mine
  DEMO_SONGS[56],  // Don't Stop Believin'
]

// Mock setlists (use createDemoSetlists() for translated version)
export const DEMO_SETLISTS: DemoSetlist[] = [
  {
    id: 'setlist-001',
    name: 'Classic Rock Essentials',
    songs: [
      { position: 1, songId: 'demo-song-001', song: DEMO_SONGS[0] },   // Stairway to Heaven
      { position: 2, songId: 'demo-song-007', song: DEMO_SONGS[6] },   // Bohemian Rhapsody
      { position: 3, songId: 'demo-song-026', song: DEMO_SONGS[25] },  // Back in Black
      { position: 4, songId: 'demo-song-013', song: DEMO_SONGS[12] },  // Sweet Home Alabama
      { position: 5, songId: 'demo-song-021', song: DEMO_SONGS[20] },  // Free Bird
      { position: 6, songId: 'demo-song-032', song: DEMO_SONGS[31] },  // Comfortably Numb
      { position: 7, songId: 'demo-song-047', song: DEMO_SONGS[46] },  // More Than a Feeling
      { position: 8, songId: 'demo-song-052', song: DEMO_SONGS[51] },  // Barracuda
    ],
    get totalDurationMs() {
      return this.songs.reduce((sum, s) => sum + s.song.durationMs, 0)
    },
  },
  {
    id: 'setlist-002',
    name: 'Saturday Night Party',
    songs: [
      { position: 1, songId: 'demo-song-008', song: DEMO_SONGS[7] },   // We Will Rock You
      { position: 2, songId: 'demo-song-044', song: DEMO_SONGS[43] },  // Start Me Up
      { position: 3, songId: 'demo-song-029', song: DEMO_SONGS[28] },  // Highway to Hell
      { position: 4, songId: 'demo-song-055', song: DEMO_SONGS[54] },  // Livin' on a Prayer
      { position: 5, songId: 'demo-song-059', song: DEMO_SONGS[58] },  // Pour Some Sugar on Me
      { position: 6, songId: 'demo-song-009', song: DEMO_SONGS[8] },   // We Are the Champions
      { position: 7, songId: 'demo-song-003', song: DEMO_SONGS[2] },   // Kashmir
      { position: 8, songId: 'demo-song-063', song: DEMO_SONGS[62] },  // Welcome to the Jungle
      { position: 9, songId: 'demo-song-069', song: DEMO_SONGS[68] },  // Enter Sandman
      { position: 10, songId: 'demo-song-070', song: DEMO_SONGS[69] }, // Master of Puppets
    ],
    get totalDurationMs() {
      return this.songs.reduce((sum, s) => sum + s.song.durationMs, 0)
    },
  },
]

/** Create translated demo setlists */
export function createDemoSetlists(names: { classicRock: string; saturdayNight: string }): DemoSetlist[] {
  return DEMO_SETLISTS.map(setlist => {
    if (setlist.id === 'setlist-001') return { ...setlist, name: names.classicRock }
    if (setlist.id === 'setlist-002') return { ...setlist, name: names.saturdayNight }
    return setlist
  })
}

